/**
 * useProposalWebSocket — React hook that mounts a ProposalWebSocket and
 * applies inbound messages to the React Query cache.
 *
 * Mount once at the OrgContext level alongside useCrmWebSocket. The hook
 * automatically (re)connects when the orgId changes and tears down the
 * connection on unmount or when disabled.
 *
 * On each event, also surfaces a debounced toast notification (suppressed
 * on /actions/* routes where entrance animations already signal arrival).
 */

import { QueryClientContext } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { isOnboardingFlowPath } from '@/lib/onboardingRoutes';

import {
  showNewProposalsSummaryToast,
  showNewProposalToast,
} from '../utils/showNewProposalToast';
import {
  applyProposalCreated,
  applyProposalUpdated,
} from './applyProposalEvent';
import { onProposalUpdatedWs } from './approvalExecutionTracker';
import { ProposalWebSocket } from './ProposalWebSocket';
import type {
  ProposalWebSocketStatus,
  WSProposalCreated,
  WSProposalUpdated,
} from './types';

const DEBOUNCE_WINDOW_MS = 3000;
const ACTIONS_ROUTE_PATTERN = /\/actions(\/|$)/;
// Cap buffer size — prevents memory bloat under unexpected burst conditions.
const MAX_BUFFER_SIZE = 50;

interface UseProposalWebSocketOptions {
  /** Org ID to connect to. */
  orgId: number | null;
  /** Whether the connection should be active (default: false — caller opts in). */
  enabled?: boolean;
}

interface UseProposalWebSocketReturn {
  /** Current connection status. */
  status: ProposalWebSocketStatus;
}

export function useProposalWebSocket({
  orgId,
  enabled = false,
}: UseProposalWebSocketOptions): UseProposalWebSocketReturn {
  // Match useCrmWebSocket: read the QueryClient via context directly so the
  // hook is safe to use in test contexts that don't wrap with QueryClientProvider.
  const queryClient = useContext(QueryClientContext);
  const [status, setStatus] = useState<ProposalWebSocketStatus>('disconnected');
  const wsRef = useRef<ProposalWebSocket | null>(null);
  // bufferRef/flushTimerRef/orgIdRef back flushBuffer's empty-deps useCallback
  // below. They're safe as an empty dependency array because flushBuffer only
  // reads and mutates these refs directly — never component state or props —
  // so its identity can stay stable across every render without going stale.
  const bufferRef = useRef<WSProposalCreated[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orgIdRef = useRef<number | null>(orgId);

  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  const flushBuffer = useCallback(() => {
    flushTimerRef.current = null;
    const buffered = bufferRef.current;
    bufferRef.current = [];
    const currentOrgId = orgIdRef.current;
    if (currentOrgId === null) {
      return;
    }
    // Suppress the toast where it would interrupt rather than inform:
    //  - /actions/*      the entrance animation already signals arrival.
    //  - the onboarding wizards: the user is mid-task on a full-screen surface,
    //    and the toast card navigates with `window.location.assign`, so a
    //    misclick drops a half-finished invitee out of onboarding entirely.
    // The socket itself stays connected in both cases, so the React Query cache
    // still receives the proposal — only the notification is withheld.
    const { pathname } = window.location;
    if (
      ACTIONS_ROUTE_PATTERN.test(pathname) ||
      isOnboardingFlowPath(pathname)
    ) {
      return;
    }
    const seen = new Set<number>();
    const unique = buffered.filter(p => {
      if (seen.has(p.id)) {
        return false;
      }
      seen.add(p.id);
      return true;
    });
    if (unique.length === 0) {
      return;
    }
    try {
      if (unique.length === 1) {
        showNewProposalToast(unique[0], currentOrgId);
      } else {
        showNewProposalsSummaryToast(unique.length, currentOrgId);
      }
    } catch {
      // Toast render error must not break subsequent WS event handling.
    }
  }, []);

  const handleCreated = useCallback(
    (proposal: WSProposalCreated) => {
      if (!queryClient || !orgId) {
        return;
      }
      applyProposalCreated(queryClient, orgId, proposal);
      if (bufferRef.current.length < MAX_BUFFER_SIZE) {
        bufferRef.current.push(proposal);
      }
      if (flushTimerRef.current === null) {
        flushTimerRef.current = setTimeout(flushBuffer, DEBOUNCE_WINDOW_MS);
      }
    },
    [queryClient, orgId, flushBuffer]
  );

  const handleUpdated = useCallback(
    (proposal: WSProposalUpdated) => {
      if (!queryClient || !orgId) {
        return;
      }
      applyProposalUpdated(queryClient, orgId, proposal);
      onProposalUpdatedWs(proposal.id, proposal.resolution);
    },
    [queryClient, orgId]
  );

  useEffect(() => {
    if (!queryClient || !enabled || !orgId) {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
        setStatus('disconnected');
      }
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      bufferRef.current = [];
      return;
    }

    const ws = new ProposalWebSocket({
      orgId,
      onCreated: handleCreated,
      onUpdated: handleUpdated,
      onStatusChange: setStatus,
    });

    wsRef.current = ws;
    ws.connect();

    return () => {
      ws.destroy();
      wsRef.current = null;
      setStatus('disconnected');
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      bufferRef.current = [];
    };
  }, [queryClient, enabled, orgId, handleCreated, handleUpdated]);

  return { status };
}
